<script setup lang="ts">
import { AFFILIATE, AFFILIATE_PROFILE, type PayoutDetails } from '~/constants/affiliate'
import { formatUsd } from '~/constants/pricing'

/**
 * The affiliate program. Signed out: the public landing page. Signed in: join
 * (accepting the terms), then the partner dashboard with the link, earnings,
 * statistics, banners and texts, payout details and payout statements.
 * The products tab lists what partners earn on (AffiliateProducts), which the
 * public page and the join step show too. Everything comes from
 * /api/affiliate/*; the rules live in
 * constants/affiliate.ts, server/utils/affiliate.ts and the terms at
 * /affiliate/terms.
 */

type CommissionStatus = 'pending' | 'available' | 'paid' | 'refunded' | 'cancelled'

interface AffiliateResponse {
  affiliate: {
    code: string
    termsVersion: string | null
    termsAcceptedAt: string | null
    payoutDetails: PayoutDetails | null
    payoutBlocker: 'terms' | 'details' | null
    needsTerms: boolean
    notifyCommissions: boolean
    displayName: string
    pageMessage: string
    discountCode: string | null
    discountPercent: number | null
    maxDiscountPercent: number
    leftAt: string | null
    endedReason: 'partner' | 'operator' | 'breach' | null
    clicks: number
    referrals: number
    createdAt: string
  } | null
  totals?: {
    orders: number
    pendingCents: number
    availableCents: number
    paidCents: number
    refundedCents: number
  }
  commissions?: {
    id: string
    kind: 'commission' | 'clawback'
    orderAmountCents: number
    commissionCents: number
    refundedCents: number
    status: CommissionStatus
    createdAt: string
    paidAt: string | null
  }[]
  payouts?: {
    id: string
    number: string
    amountCents: number
    commissions: number
    method: string
    createdAt: string
  }[]
  terms: {
    version: string
    effectiveOn: string
    maxDiscountPercent: number
  }
}

type Tab = 'overview' | 'products' | 'stats' | 'materials' | 'payout'

definePageMeta({ layout: false })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const user = useSupabaseUser()
const { show: showSnackbar } = useSnackbar()
const { canonicalFor } = useSiteUrl()

useSeoMeta({
  title: () => t('seo.affiliate.title'),
  description: () => t('seo.affiliate.description'),
  ogTitle: () => t('seo.affiliate.title'),
  ogDescription: () => t('seo.affiliate.description'),
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/partnersky-program', de: '/partnerprogramm', pl: '/program-partnerski' } })
useHead({ link: [{ rel: 'canonical', key: 'canonical', href: canonicalFor(useLocalizedPath()('/affiliate')) }] })

const { data, pending, refresh } = await useFetch<AffiliateResponse>('/api/affiliate/me', {
  server: false,
  immediate: !!user.value,
})
watch(() => user.value?.id, (id) => {
  if (id) refresh()
})

const rate = `${Math.round(AFFILIATE.commissionRate * 100)} %`
const minPayout = formatUsd(AFFILIATE.minPayoutCents / 100)
const money = (cents: number) => formatUsd(cents / 100)
const date = (iso: string) => new Date(iso).toLocaleDateString()

const howItWorks = computed(() => [
  t('affiliate.how.share'),
  t('affiliate.how.buy', { days: AFFILIATE.cookieDays }),
  t('affiliate.how.earn', { rate }),
  t('affiliate.how.payout', { hold: AFFILIATE.holdDays, min: minPayout }),
])

const affiliate = computed(() => data.value?.affiliate ?? null)
const active = computed(() => !!affiliate.value && !affiliate.value.leftAt)
const termsEffectiveOn = computed(() => data.value ? date(`${data.value.terms.effectiveOn}T00:00:00Z`) : '')

// The tab lives in the URL so a link can open, e.g., the payout form directly.
// A partner who left keeps the overview and payouts only.
const tabs = computed<Tab[]>(() => active.value ? ['overview', 'products', 'stats', 'materials', 'payout'] : ['overview', 'payout'])
const tab = computed<Tab>({
  get: () => tabs.value.includes(route.query.tab as Tab) ? route.query.tab as Tab : 'overview',
  set: value => router.replace({ query: { ...route.query, tab: value === 'overview' ? undefined : value } }),
})

// ---- Joining (and joining again after leaving) ----
// The button reads "Accept the terms and join", with the terms linked under it:
// clicking it is the acceptance, recorded with its version and time.
const joining = ref(false)
async function join() {
  joining.value = true
  try {
    await $fetch('/api/affiliate/join', { method: 'POST', body: { acceptTerms: true } })
    await refresh()
  } catch {
    showSnackbar(t('affiliate.joinError'))
  } finally {
    joining.value = false
  }
}

// ---- New terms version ----
const acceptingTerms = ref(false)
async function acceptNewTerms() {
  acceptingTerms.value = true
  try {
    await $fetch('/api/affiliate/accept-terms', { method: 'POST' })
    await refresh()
  } catch {
    showSnackbar(t('affiliate.settings.error.generic'))
  } finally {
    acceptingTerms.value = false
  }
}

// ---- Settings: link code, their page and notifications ----
const code = ref('')
const notifyCommissions = ref(true)
const displayName = ref('')
const pageMessage = ref('')
watch(affiliate, (value) => {
  code.value = value?.code ?? ''
  notifyCommissions.value = value?.notifyCommissions ?? true
  displayName.value = value?.displayName ?? ''
  pageMessage.value = value?.pageMessage ?? ''
}, { immediate: true })

const saving = ref(false)
const saveError = ref('')
const dirty = computed(() => !!affiliate.value
  && (code.value !== affiliate.value.code
    || notifyCommissions.value !== affiliate.value.notifyCommissions
    || displayName.value.trim() !== affiliate.value.displayName
    || pageMessage.value.trim() !== affiliate.value.pageMessage))

async function saveSettings() {
  if (!affiliate.value) return
  saving.value = true
  saveError.value = ''
  try {
    const body: Record<string, unknown> = {}
    if (code.value !== affiliate.value.code) body.code = code.value
    if (notifyCommissions.value !== affiliate.value.notifyCommissions) body.notifyCommissions = notifyCommissions.value
    if (displayName.value.trim() !== affiliate.value.displayName) body.displayName = displayName.value
    if (pageMessage.value.trim() !== affiliate.value.pageMessage) body.pageMessage = pageMessage.value
    await $fetch('/api/affiliate/me', { method: 'PATCH', body })
    await refresh()
    showSnackbar(t('affiliate.settings.saved'))
  } catch (err: unknown) {
    const reason = (err as { data?: { statusMessage?: string } })?.data?.statusMessage ?? ''
    const key = ['INVALID_CODE', 'CODE_TAKEN'].includes(reason) ? reason : 'generic'
    saveError.value = t(`affiliate.settings.error.${key}`)
  } finally {
    saving.value = false
  }
}

// ---- Leaving ----
const leaving = ref(false)
async function leave() {
  if (!window.confirm(t('affiliate.leave.confirm', { min: minPayout }))) return
  leaving.value = true
  try {
    await $fetch('/api/affiliate/leave', { method: 'POST' })
    await refresh()
    tab.value = 'overview'
    showSnackbar(t('affiliate.leave.done'))
  } catch {
    showSnackbar(t('affiliate.settings.error.generic'))
  } finally {
    leaving.value = false
  }
}

// "Payout details missing" waits until there is something to pay out; before
// that, the getting-started list asks for them as one of the first steps.
const hasEarnings = computed(() => ((data.value?.totals?.pendingCents ?? 0) + (data.value?.totals?.availableCents ?? 0)) > 0)

// ---- Getting started: where each step's button leads ----
const linkSection = ref<HTMLElement | null>(null)
const discountSection = ref<HTMLElement | null>(null)
const tabsSection = ref<HTMLElement | null>(null)
async function goTo(target: 'link' | 'materials' | 'discount' | 'payout') {
  if (target === 'materials' || target === 'payout') {
    tab.value = target
    await nextTick()
    tabsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }
  const el = target === 'link' ? linkSection.value : discountSection.value
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const balance = computed(() => {
  const totals = data.value?.totals
  if (!totals) return []
  return [
    { label: t('affiliate.stats.pending'), value: money(totals.pendingCents), hint: t('affiliate.stats.pendingHint', { hold: AFFILIATE.holdDays }) },
    { label: t('affiliate.stats.available'), value: money(totals.availableCents), hint: t('affiliate.stats.availableHint', { min: minPayout }), accent: true },
    { label: t('affiliate.stats.paid'), value: money(totals.paidCents) },
    { label: t('affiliate.stats.orders'), value: String(totals.orders), hint: t('affiliate.stats.allTime') },
  ]
})

const statusClass: Record<CommissionStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  available: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  paid: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  refunded: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}
</script>

<template>
  <NuxtLayout name="default" :show-back-button="false">
    <section class="section-space-sm">
      <div class="shell text-base">
        <div class="max-w-3xl border-b border-line pb-10 dark:border-gray-800">
          <h1 class="!text-3xl md:!text-4xl">{{ t('affiliate.title') }}</h1>
          <p class="lead mt-3">{{ t('affiliate.subtitle', { rate }) }}</p>
        </div>

        <!-- Signed out: the public page -->
        <AffiliateLanding v-if="!user" />

        <div v-else-if="pending && !data" class="flex items-center justify-center gap-3 py-16 text-muted dark:text-gray-400">
          <i class="bi bi-arrow-repeat animate-spin text-2xl" />
        </div>

        <!-- Not a member yet: how it works, the terms and one button, then what they would earn on -->
        <template v-else-if="!affiliate">
          <div class="grid gap-10 py-10 lg:grid-cols-2">
            <div>
              <h2 class="!text-2xl">{{ t('affiliate.how.title') }}</h2>
              <ol class="mt-6 space-y-4">
                <li v-for="(line, index) in howItWorks" :key="index" class="flex gap-4">
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft/40 font-title font-bold text-ink dark:bg-accent/20 dark:text-accent-soft">{{ index + 1 }}</span>
                  <span class="pt-1">{{ line }}</span>
                </li>
              </ol>
              <!-- One click: the button says it accepts the terms, linked right under it -->
              <SofaxBtn
                class="mt-8"
                :label="joining ? t('affiliate.joining') : t('affiliate.join')"
                :disabled="joining"
                @click="join"
              />
              <p class="mt-3 text-sm text-body dark:text-gray-300">
                {{ t('affiliate.joinNote') }}
                <LocaleLink to="/affiliate/terms" target="_blank" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
              </p>
              <p class="mt-6 text-sm text-muted dark:text-gray-400">
                {{ t('affiliate.rules') }}
                <LocaleLink to="/affiliate/terms" target="_blank" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
              </p>
            </div>
            <div class="flex items-center justify-center rounded-[0.625rem] bg-surface p-10 dark:bg-gray-800">
              <div class="text-center">
                <div class="font-title text-7xl font-bold text-accent">{{ rate }}</div>
                <div class="mt-3 text-lg text-body dark:text-gray-300">{{ t('affiliate.how.earn', { rate }) }}</div>
              </div>
            </div>
          </div>
          <AffiliateProducts class="border-t border-line py-10 dark:border-gray-800" />
        </template>

        <!-- Partner dashboard -->
        <template v-else>
          <!-- Membership ended -->
          <div v-if="affiliate.leftAt" class="mt-8 rounded-[0.625rem] border border-line bg-surface p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 class="!text-xl">{{ t('affiliate.ended.title', { date: date(affiliate.leftAt) }) }}</h2>
            <p class="mt-2 text-sm text-body dark:text-gray-300">
              {{ affiliate.endedReason === 'breach' ? t('affiliate.ended.breach') : t('affiliate.ended.balance', { min: minPayout }) }}
            </p>
            <template v-if="affiliate.endedReason !== 'breach'">
              <button type="button" class="btn btn-accent btn-sm mt-5" :disabled="joining" @click="join">
                {{ t('affiliate.ended.rejoin') }}
              </button>
              <p class="mt-2 text-sm text-body dark:text-gray-300">
                {{ t('affiliate.joinNote') }}
                <LocaleLink to="/affiliate/terms" target="_blank" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
              </p>
            </template>
          </div>

          <!-- New terms, or payout details still missing -->
          <div
            v-else-if="affiliate.needsTerms || (affiliate.payoutBlocker && hasEarnings)"
            class="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[0.625rem] border p-5 text-sm"
            :class="affiliate.needsTerms
              ? 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-200'
              : 'border-line bg-surface text-body dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'"
            role="status"
          >
            <span v-if="affiliate.needsTerms" class="flex flex-1 items-start gap-3">
              <i class="bi bi-exclamation-triangle-fill mt-0.5" aria-hidden="true" />
              <span>
                {{ affiliate.payoutBlocker === 'terms' ? t('affiliate.newTerms') : t('affiliate.upcomingTerms', { date: termsEffectiveOn }) }}
                <LocaleLink to="/affiliate/terms" target="_blank" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
              </span>
            </span>
            <span v-else class="flex flex-1 items-start gap-3">
              <i class="bi bi-info-circle-fill mt-0.5" aria-hidden="true" />
              {{ t('affiliate.payoutDetailsMissing') }}
            </span>
            <button
              v-if="affiliate.needsTerms"
              type="button"
              class="btn btn-accent btn-sm !w-auto shrink-0"
              :disabled="acceptingTerms"
              @click="acceptNewTerms"
            >
              {{ t('affiliate.acceptNewTerms') }}
            </button>
            <button v-else-if="tab !== 'payout'" type="button" class="btn btn-dark btn-sm !w-auto shrink-0" @click="tab = 'payout'">
              {{ t('affiliate.fillPayoutDetails') }}
            </button>
          </div>

          <!-- A new partner's first steps, until they are all done -->
          <AffiliateGettingStarted
            v-if="active"
            class="mt-8"
            :clicks="affiliate.clicks"
            :has-discount-code="!!affiliate.discountCode"
            :has-payout-details="!!affiliate.payoutDetails"
            :orders="data?.totals?.orders ?? 0"
            @go="goTo"
          />

          <!-- Link, always in view while the membership runs -->
          <div v-if="active" ref="linkSection" class="mt-8 scroll-mt-24">
            <AffiliateLinkBuilder :code="affiliate.code" :discount-code="affiliate.discountCode" :discount-percent="affiliate.discountPercent" />
          </div>
          <div v-if="active" ref="discountSection" class="mt-6 scroll-mt-24">
            <AffiliateDiscountCode
              :code="affiliate.code"
              :discount-code="affiliate.discountCode"
              :discount-percent="affiliate.discountPercent"
              :max-percent="affiliate.maxDiscountPercent"
              :needs-terms="affiliate.needsTerms"
              @saved="refresh"
            />
          </div>

          <!-- Tabs -->
          <div ref="tabsSection" class="mt-8 flex scroll-mt-24 gap-1 overflow-x-auto border-b border-line dark:border-gray-700" role="tablist">
            <button
              v-for="name in tabs"
              :key="name"
              type="button"
              role="tab"
              :aria-selected="tab === name"
              class="-mb-px whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors duration-300"
              :class="tab === name ? 'border-accent text-ink dark:text-white' : 'border-transparent text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
              @click="tab = name"
            >
              {{ t(`affiliate.tabs.${name}`) }}
            </button>
          </div>

          <div class="mt-8">
            <!-- Overview: balance, commissions, settings -->
            <template v-if="tab === 'overview'">
              <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div
                  v-for="stat in balance"
                  :key="stat.label"
                  class="rounded-[0.625rem] border p-5"
                  :class="stat.accent ? 'border-accent bg-accent-soft/20 dark:bg-accent/10' : 'border-line dark:border-gray-700'"
                >
                  <div class="text-sm text-muted dark:text-gray-400">{{ stat.label }}</div>
                  <div class="mt-1 font-title text-2xl font-bold text-ink dark:text-white">{{ stat.value }}</div>
                  <div v-if="stat.hint" class="mt-1 text-xs text-muted dark:text-gray-400">{{ stat.hint }}</div>
                </div>
              </div>

              <div class="mt-10 grid gap-10 lg:grid-cols-3">
                <div :class="active ? 'lg:col-span-2' : 'lg:col-span-3'">
                  <h2 class="!text-xl">{{ t('affiliate.commissions.title') }}</h2>
                  <p v-if="!data?.commissions?.length" class="mt-4 text-muted dark:text-gray-400">
                    {{ t('affiliate.commissions.empty') }}
                  </p>
                  <div v-else class="mt-4 overflow-x-auto rounded-[0.625rem] border border-line dark:border-gray-700">
                    <table class="w-full text-sm">
                      <thead class="bg-surface font-title font-semibold text-ink dark:bg-gray-800 dark:text-white">
                        <tr>
                          <th class="px-4 py-3 text-left">{{ t('affiliate.commissions.date') }}</th>
                          <th class="px-4 py-3 text-right">{{ t('affiliate.commissions.order') }}</th>
                          <th class="px-4 py-3 text-right">{{ t('affiliate.commissions.commission') }}</th>
                          <th class="px-4 py-3 text-left">{{ t('affiliate.commissions.status') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in data.commissions" :key="row.id" class="border-t border-line dark:border-gray-700">
                          <td class="px-4 py-3">
                            {{ date(row.createdAt) }}
                            <div v-if="row.kind === 'clawback'" class="text-xs text-muted dark:text-gray-400">{{ t('affiliate.commissions.clawback') }}</div>
                            <div v-else-if="row.refundedCents && row.status !== 'refunded'" class="text-xs text-muted dark:text-gray-400">
                              {{ t('affiliate.commissions.partlyRefunded', { amount: money(row.refundedCents) }) }}
                            </div>
                          </td>
                          <td class="px-4 py-3 text-right">{{ money(row.orderAmountCents) }}</td>
                          <td class="px-4 py-3 text-right font-semibold" :class="{ 'text-red-600 dark:text-red-400': row.commissionCents < 0 }">{{ money(row.commissionCents) }}</td>
                          <td class="px-4 py-3">
                            <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass[row.status]">
                              {{ t(`affiliate.commissions.statuses.${row.status}`) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div v-if="active" class="space-y-8">
                  <form class="space-y-4" @submit.prevent="saveSettings">
                    <h2 class="!text-xl">{{ t('affiliate.settings.title') }}</h2>
                    <SofaxInput v-model="code" :label="t('affiliate.settings.code')" :hint="t('affiliate.settings.codeHint')" :maxlength="32" />
                    <SofaxInput v-model="displayName" :label="t('affiliate.settings.displayName')" :hint="t('affiliate.settings.displayNameHint')" :maxlength="AFFILIATE_PROFILE.nameMax" />
                    <SofaxTextarea v-model="pageMessage" :label="t('affiliate.settings.pageMessage')" :hint="t('affiliate.settings.pageMessageHint', { max: AFFILIATE_PROFILE.messageMax })" :maxlength="AFFILIATE_PROFILE.messageMax" :rows="3" />
                    <LocaleLink :to="`/p/${affiliate.code}`" target="_blank" class="link inline-flex items-center gap-1 text-sm">
                      {{ t('affiliate.settings.viewPage') }}
                      <i class="bi bi-box-arrow-up-right" aria-hidden="true" />
                    </LocaleLink>
                    <label class="flex cursor-pointer items-start gap-3 text-sm">
                      <input v-model="notifyCommissions" type="checkbox" class="field-check mt-0.5">
                      <span>{{ t('affiliate.settings.notifyCommissions') }}</span>
                    </label>
                    <SofaxNotice v-if="saveError" type="error">{{ saveError }}</SofaxNotice>
                    <SofaxBtn type="submit" :label="t('affiliate.settings.save')" :disabled="saving || !dirty" />
                  </form>

                  <p class="text-sm text-muted dark:text-gray-400">
                    {{ t('affiliate.rules') }}
                    <LocaleLink to="/affiliate/terms" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
                  </p>

                  <div class="border-t border-line pt-6 dark:border-gray-700">
                    <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.leave.title') }}</h3>
                    <p class="mt-1 text-sm text-muted dark:text-gray-400">{{ t('affiliate.leave.text', { min: minPayout }) }}</p>
                    <button type="button" class="link mt-3 text-sm !text-red-600 dark:!text-red-400" :disabled="leaving" @click="leave">
                      {{ t('affiliate.leave.button') }}
                    </button>
                  </div>
                </div>
              </div>
            </template>

            <AffiliateProducts v-else-if="tab === 'products'" />

            <AffiliateStatsPanel v-else-if="tab === 'stats'" />

            <AffiliateMaterials
              v-else-if="tab === 'materials'"
              :code="affiliate.code"
              :discount-code="affiliate.discountCode"
              :discount-percent="affiliate.discountPercent"
            />

            <!-- Payouts: history with statements, and the details they go to -->
            <div v-else-if="tab === 'payout'" class="space-y-12">
              <section>
                <h2 class="!text-xl">{{ t('affiliate.payouts.title') }}</h2>
                <p v-if="!data?.payouts?.length" class="mt-3 text-sm text-muted dark:text-gray-400">{{ t('affiliate.payouts.empty', { min: minPayout }) }}</p>
                <div v-else class="mt-4 overflow-x-auto rounded-[0.625rem] border border-line dark:border-gray-700">
                  <table class="w-full text-sm">
                    <thead class="bg-surface font-title font-semibold text-ink dark:bg-gray-800 dark:text-white">
                      <tr>
                        <th class="px-4 py-3 text-left">{{ t('affiliate.payouts.number') }}</th>
                        <th class="px-4 py-3 text-left">{{ t('affiliate.commissions.date') }}</th>
                        <th class="px-4 py-3 text-left">{{ t('affiliate.payout.method') }}</th>
                        <th class="px-4 py-3 text-right">{{ t('affiliate.payouts.amount') }}</th>
                        <th class="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payout in data.payouts" :key="payout.id" class="border-t border-line dark:border-gray-700">
                        <td class="px-4 py-3 font-mono">{{ payout.number }}</td>
                        <td class="px-4 py-3">{{ date(payout.createdAt) }}</td>
                        <td class="px-4 py-3">{{ t(`affiliate.payout.methods.${payout.method}`) }}</td>
                        <td class="px-4 py-3 text-right font-semibold">{{ money(payout.amountCents) }}</td>
                        <td class="px-4 py-3 text-right">
                          <LocaleLink :to="`/affiliate/payouts/${payout.id}`" class="link whitespace-nowrap">{{ t('affiliate.payouts.statement') }}</LocaleLink>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="grid gap-10 lg:grid-cols-3">
                <div class="lg:col-span-2">
                  <h2 class="!text-xl">{{ t('affiliate.payout.title') }}</h2>
                  <p class="mt-2 text-sm text-muted dark:text-gray-400">{{ t('affiliate.payout.subtitle', { min: minPayout, hold: AFFILIATE.holdDays }) }}</p>
                  <AffiliatePayoutDetailsForm class="mt-6" :details="affiliate.payoutDetails" @saved="refresh" />
                </div>
                <aside class="h-fit rounded-[0.625rem] bg-surface p-6 text-sm dark:bg-gray-800">
                  <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.payout.howTitle') }}</h3>
                  <ul class="mt-3 list-disc space-y-2 pl-5 text-body dark:text-gray-300">
                    <li>{{ t('affiliate.payout.how.schedule', { min: minPayout }) }}</li>
                    <li>{{ t('affiliate.payout.how.statement') }}</li>
                    <li>{{ t('affiliate.payout.how.currency') }}</li>
                    <li>{{ t('affiliate.payout.how.taxes') }}</li>
                    <li>{{ t('affiliate.payout.how.business') }}</li>
                  </ul>
                </aside>
              </section>
            </div>
          </div>
        </template>
      </div>
    </section>
  </NuxtLayout>
</template>
